import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useOrderStore } from '../../store/orderStore';
import { Package, ChevronRight, Trash2, RefreshCw } from 'lucide-react';
import { Order } from '../../types';

export default function AdminOrders() {
  const { getAllOrders, deleteOrder, updateOrderStatus } = useOrderStore();
  const [orders, setOrders] = useState<Order[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<string>('pending');
  const [bulkUpdating, setBulkUpdating] = useState(false);

  useEffect(() => {
    const fetchOrders = async () => {
      const allOrders = await getAllOrders();
      setOrders(allOrders);
      setFilteredOrders(allOrders);
      setLoading(false);
    };
    fetchOrders();
  }, [getAllOrders]);
  
  // Filter orders based on search term and status filter
  useEffect(() => {
    let filtered = orders;
    
    // Apply search term filter
    if (searchTerm.trim()) {
      const lowercasedSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(order => 
        order.id.toLowerCase().includes(lowercasedSearch) ||
        (order.userName || '').toLowerCase().includes(lowercasedSearch) ||
        (order.userEmail || '').toLowerCase().includes(lowercasedSearch) ||
        (order.customerEmail || '').toLowerCase().includes(lowercasedSearch) ||
        (order.status || '').toLowerCase().includes(lowercasedSearch)
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }
    
    setFilteredOrders(filtered);
  }, [searchTerm, statusFilter, orders]);
  
  // Define all possible statuses for the filter dropdown
  const allStatuses = ['all', 'pending', 'dispatched', 'completed', 'cancelled', 'returned'];
  
  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  // Handle order deletion
  const handleDeleteOrder = async (orderId: string) => {
    setDeleting(orderId);
    try {
      await deleteOrder(orderId);
      // Update local state to remove the deleted order
      setOrders(prev => prev.filter(order => order.id !== orderId));
      setFilteredOrders(prev => prev.filter(order => order.id !== orderId));
    } catch (error) {
      console.error('Failed to delete order:', error);
    } finally {
      setDeleting(null);
      setDeleteConfirm(null);
    }
  };

  // Handle status update
  const handleStatusUpdate = async (orderId: string, newStatus: string) => {
    setUpdatingStatus(orderId);
    try {
      await updateOrderStatus(orderId, newStatus);
      // Update local state
      setOrders(prev => prev.map(order => 
        order.id === orderId ? { ...order, status: newStatus as Order['status'] } : order
      ));
      setFilteredOrders(prev => prev.map(order => 
        order.id === orderId ? { ...order, status: newStatus as Order['status'] } : order
      ));
    } catch (error) {
      console.error('Failed to update order status:', error);
    } finally {
      setUpdatingStatus(null);
    }
  };

  // Handle checkbox toggle
  const toggleOrderSelection = (orderId: string) => {
    setSelectedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  // Handle select all toggle
  const toggleSelectAll = () => {
    if (selectedOrders.size === filteredOrders.length) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(filteredOrders.map(order => order.id)));
    }
  };

  // Handle bulk status update
  const handleBulkStatusUpdate = async () => {
    if (selectedOrders.size === 0) return;
    
    setBulkUpdating(true);
    try {
      // Update all selected orders
      await Promise.all(
        Array.from(selectedOrders).map(orderId => 
          updateOrderStatus(orderId, bulkStatus)
        )
      );
      
      // Update local state
      setOrders(prev => prev.map(order => 
        selectedOrders.has(order.id) ? { ...order, status: bulkStatus as Order['status'] } : order
      ));
      setFilteredOrders(prev => prev.map(order => 
        selectedOrders.has(order.id) ? { ...order, status: bulkStatus as Order['status'] } : order
      ));
      
      // Clear selection
      setSelectedOrders(new Set());
    } catch (error) {
      console.error('Failed to update orders:', error);
    } finally {
      setBulkUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-8">All Orders</h2>
        <div className="text-center py-12">
          <p>Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-900">All Orders</h2>
          <p className="text-sm text-gray-500">
            {filteredOrders.length} of {orders.length} orders
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search input */}
          <div className="relative flex-grow">
            <input
              type="text"
              placeholder="Search by ID, name, email, or status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          
          {/* Status filter */}
          <div className="w-full sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              {allStatuses.map(status => (
                <option key={status} value={status}>
                  {status === 'all' ? 'All Statuses' : status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </div>
          
          {/* Clear filters button */}
          {(searchTerm || statusFilter !== 'all') && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedOrders.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <p className="text-sm font-medium text-blue-900">
                {selectedOrders.size} order{selectedOrders.size > 1 ? 's' : ''} selected
              </p>
              <button
                onClick={() => setSelectedOrders(new Set())}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Clear selection
              </button>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">Update status to:</label>
              <select
                value={bulkStatus}
                onChange={(e) => setBulkStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="pending">Pending</option>
                <option value="dispatched">Dispatched</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="returned">Returned</option>
              </select>
              <button
                onClick={handleBulkStatusUpdate}
                disabled={bulkUpdating}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {bulkUpdating ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Selected'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {orders.length === 0 ? (
        <div className="text-center py-12">
          <Package className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No orders</h3>
          <p className="mt-1 text-sm text-gray-500">
            There are no orders in the system yet.
          </p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="text-center py-12">
          <Package className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No matching orders</h3>
          <p className="mt-1 text-sm text-gray-500">
            No orders match your search criteria. Try a different search term.
          </p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {/* Select All Header */}
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={selectedOrders.size === filteredOrders.length && filteredOrders.length > 0}
                onChange={toggleSelectAll}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">
                Select All ({filteredOrders.length})
              </span>
            </label>
          </div>
          
          <ul className="divide-y divide-gray-200">
            {filteredOrders.map((order) => (
              <li key={order.id}>
                <div className="flex hover:bg-gray-50">
                  {/* Checkbox */}
                  <div className="flex items-center px-4">
                    <input
                      type="checkbox"
                      checked={selectedOrders.has(order.id)}
                      onChange={() => toggleOrderSelection(order.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                    />
                  </div>
                  
                  <Link
                    to={`/admin/orders/${order.id}`}
                    className="flex-1 block"
                  >
                    <div className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <img
                            src={order.items && order.items.length > 0 ? order.items[0]?.imageUrl : '/placeholder.jpg'}
                            alt={order.items && order.items.length > 0 ? order.items[0]?.name : 'Order item'}
                            className="h-16 w-16 object-cover rounded"
                          />
                          <div className="ml-4">
                            <p className="text-sm font-medium text-gray-900">
                              Order #{order.id}
                            </p>
                            <p className="text-sm text-gray-500">
                              Customer ID: {order.userId}
                            </p>
                            <p className="text-sm text-gray-500">
                              {new Date(order.createdAt).toLocaleDateString()}
                            </p>
                            <div className="flex items-center gap-2">
                              <select
                                value={order.status}
                                onChange={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleStatusUpdate(order.id, e.target.value);
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                disabled={updatingStatus === order.id}
                                className={`text-xs px-2 py-1 rounded-full border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                  order.status === 'pending' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                                  order.status === 'dispatched' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                                  order.status === 'completed' ? 'bg-green-100 text-green-800 border-green-300' :
                                  order.status === 'cancelled' ? 'bg-red-100 text-red-800 border-red-300' :
                                  order.status === 'returned' ? 'bg-gray-100 text-gray-800 border-gray-300' :
                                  'bg-gray-100 text-gray-800 border-gray-300'
                                } disabled:opacity-50 cursor-pointer`}
                              >
                                <option value="pending">Pending</option>
                                <option value="dispatched">Dispatched</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="returned">Returned</option>
                              </select>
                              {updatingStatus === order.id && (
                                <RefreshCw className="h-3 w-3 text-gray-400 animate-spin" />
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end">
                          <p className="text-sm font-medium text-gray-900 mb-1">
                            NPR {(order.finalPrice || order.totalPrice || 0).toFixed(2)}
                          </p>
                          <ChevronRight className="h-5 w-5 text-gray-400" />
                        </div>
                      </div>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          {order.shippingAddress ? 
                            `Shipping to: ${order.shippingAddress.city}, ${order.shippingAddress.state}` :
                            'No shipping address provided'
                          }
                        </p>
                      </div>
                    </div>
                  </Link>
                  
                  {/* Delete button */}
                  <div className="flex items-center px-4">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        setDeleteConfirm(order.id);
                      }}
                      disabled={deleting === order.id}
                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                      title="Delete order"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Delete Order</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete order #{deleteConfirm}? This action cannot be undone.
                </p>
              </div>
              <div className="flex justify-center gap-4 mt-4">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  disabled={deleting === deleteConfirm}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteOrder(deleteConfirm)}
                  disabled={deleting === deleteConfirm}
                  className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {deleting === deleteConfirm ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
